import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Close } from '@mui/icons-material';
import { reportService } from '../services/reportService';
import { customerService } from '../services/customerService';
import { formatCurrency } from '../components/CurrencyTextField';
import { format } from 'date-fns';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';

const ARAging = () => {
  const DEFAULT_COL_WIDTHS = {
    customer: 220,
    invoice: 140,
    invoiceDate: 120,
    dueDate: 120,
    total: 120,
    balance: 120,
    age: 100
  };
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('');
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState('due_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_ar_aging_col_widths',
    minWidths: { customer: 150, invoice: 100, invoiceDate: 100, dueDate: 100, total: 100, balance: 100, age: 90 }
  });

  useEffect(() => {
    customerService.getAll({ limit: 500 }).then((r) => setCustomers(r?.data ?? [])).catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    loadReport();
  }, [customerFilter, asOf]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { as_of: asOf };
      if (customerFilter) params.customer_id = customerFilter;
      const res = await reportService.getARAging(params);
      setData(res.data);
      setSelectedInvoice(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load AR aging report');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => (d ? format(new Date(d), 'MM/dd/yyyy') : '—');
  const customerName = (c) =>
    c.company_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || `#${c.id}`;
  const getBucketColor = (b) => {
    const m = { Current: 'success', '1-30': 'info', '31-60': 'warning', '61-90': 'error', '90+': 'error' };
    return m[b] || 'default';
  };

  const invoices = data?.invoices ?? [];
  const summary = data?.summary ?? {};
  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedInvoices = useMemo(() => {
    const list = [...invoices];
    const getter = (inv) => {
      switch (sortKey) {
        case 'customer': return inv.company_name || `${inv.first_name || ''} ${inv.last_name || ''}`.trim();
        case 'invoice_number': return inv.identifier ?? inv.invoice_number ?? '';
        case 'invoice_date': return inv.invoice_date ? new Date(inv.invoice_date).getTime() : 0;
        case 'due_date': return inv.due_date ? new Date(inv.due_date).getTime() : 0;
        case 'total_amount': return Number(inv.total_amount) || 0;
        case 'balance_due': return Number(inv.balance_due) || 0;
        case 'age_bucket': return inv.age_bucket ?? '';
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [invoices, sortKey, sortDirection]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          AR Aging Report
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {/* Selected Invoice Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedInvoice?.identifier ?? selectedInvoice?.invoice_number ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
            <Typography variant="body2">
              {selectedInvoice?.customer_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customers/${selectedInvoice.customer_id}`)}
                >
                  {selectedInvoice.company_name || [selectedInvoice.first_name, selectedInvoice.last_name].filter(Boolean).join(' ') || `#${selectedInvoice.customer_id}`}
                </Button>
              ) : (
                selectedInvoice
                  ? (selectedInvoice.company_name || [selectedInvoice.first_name, selectedInvoice.last_name].filter(Boolean).join(' ') || '—')
                  : '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice Date</Typography>
            <Typography variant="body2">{selectedInvoice ? formatDate(selectedInvoice.invoice_date) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Due Date</Typography>
            <Typography variant="body2">{selectedInvoice ? formatDate(selectedInvoice.due_date) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Total</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedInvoice ? formatCurrency(selectedInvoice.total_amount, '—') : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Balance Due</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedInvoice ? formatCurrency(selectedInvoice.balance_due, '—') : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Age</Typography>
            {selectedInvoice?.age_bucket ? (
              <Chip label={selectedInvoice.age_bucket} color={getBucketColor(selectedInvoice.age_bucket)} size="small" sx={{ textTransform: 'capitalize' }} />
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {selectedInvoice?.id && (
            <Button size="small" variant="outlined" onClick={() => navigate(`/customer-invoices/${selectedInvoice.id}`)}>
              View Invoice
            </Button>
          )}
          <IconButton size="small" onClick={() => setSelectedInvoice(null)} disabled={!selectedInvoice} sx={{ ml: 0.5 }} title="Clear selection">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField
            size="small"
            label="As of date"
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Customer"
            >
              <MenuItem value="">All</MenuItem>
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {customerName(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={loadReport}>
            Refresh
          </Button>
        </Box>
      </Paper>

      {Object.keys(summary).length > 0 && summary.total !== undefined && (
        <Paper sx={{ p: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Summary by Age</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {['Current', '1-30', '31-60', '61-90', '90+'].map((b) => (
              summary[b] !== undefined && (
                <Box key={b}>
                  <Typography variant="caption" color="text.secondary">{b}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(summary[b])}</Typography>
                </Box>
              )
            ))}
            <Box sx={{ ml: 'auto' }}>
              <Typography variant="caption" color="text.secondary">Total AR</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(summary.total)}</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <ResizableHeaderCell width={colWidths.customer} onResizeStart={(e) => startResize('customer', e)} sortable sortActive={sortKey === 'customer'} sortDirection={sortDirection} onSort={() => handleSort('customer')}>Customer</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.invoice} onResizeStart={(e) => startResize('invoice', e)} sortable sortActive={sortKey === 'invoice_number'} sortDirection={sortDirection} onSort={() => handleSort('invoice_number')}>Invoice #</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.invoiceDate} onResizeStart={(e) => startResize('invoiceDate', e)} sortable sortActive={sortKey === 'invoice_date'} sortDirection={sortDirection} onSort={() => handleSort('invoice_date')}>Invoice Date</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.dueDate} onResizeStart={(e) => startResize('dueDate', e)} sortable sortActive={sortKey === 'due_date'} sortDirection={sortDirection} onSort={() => handleSort('due_date')}>Due Date</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.total} onResizeStart={(e) => startResize('total', e)} align="right" sortable sortActive={sortKey === 'total_amount'} sortDirection={sortDirection} onSort={() => handleSort('total_amount')}>Total</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.balance} onResizeStart={(e) => startResize('balance', e)} align="right" sortable sortActive={sortKey === 'balance_due'} sortDirection={sortDirection} onSort={() => handleSort('balance_due')}>Balance</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.age} onResizeStart={(e) => startResize('age', e)} sortable sortActive={sortKey === 'age_bucket'} sortDirection={sortDirection} onSort={() => handleSort('age_bucket')}>Age</ResizableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    No outstanding invoices
                  </TableCell>
                </TableRow>
              ) : (
                displayedInvoices.map((inv) => (
                  <TableRow
                    key={inv.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedInvoice?.id === inv.id ? 'action.selected' : 'inherit'
                    }}
                    onClick={() => setSelectedInvoice(inv)}
                    onDoubleClick={() => inv.id && navigate(`/customer-invoices/${inv.id}`)}
                  >
                    <TableCell sx={{ width: colWidths.customer }}>
                      {inv.company_name || [inv.first_name, inv.last_name].filter(Boolean).join(' ') || '—'}
                    </TableCell>
                    <TableCell sx={{ width: colWidths.invoice }}>{inv.identifier ?? inv.invoice_number}</TableCell>
                    <TableCell sx={{ width: colWidths.invoiceDate }}>{formatDate(inv.invoice_date)}</TableCell>
                    <TableCell sx={{ width: colWidths.dueDate }}>{formatDate(inv.due_date)}</TableCell>
                    <TableCell sx={{ width: colWidths.total }} align="right">{formatCurrency(inv.total_amount)}</TableCell>
                    <TableCell sx={{ width: colWidths.balance }} align="right">{formatCurrency(inv.balance_due)}</TableCell>
                    <TableCell sx={{ width: colWidths.age }}>
                      <Chip
                        label={inv.age_bucket}
                        color={getBucketColor(inv.age_bucket)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default ARAging;
